import React from 'react'
import {Box, Skeleton, Stack, Typography} from '@mui/material'
import {alpha, useTheme} from '@mui/material/styles'
import {useNavigate} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useGetMeterReadingDashboardQuery} from 'store/Store'
import SectionCard from 'components/Page/Dashboard/SectionCard'
import {
    buildListLink,
    formatNumber,
    getUnit,
    todayRange,
} from 'components/Page/Dashboard/dashboardFormat'

const TopConsumers = ({category}) => {
    const {t} = useTranslation()
    const {palette} = useTheme()
    const navigate = useNavigate()
    const {data, isLoading} = useGetMeterReadingDashboardQuery({category})
    const consumers = data?.topConsumers || []
    const max = Math.max(...consumers.map(({usage}) => usage), 0)
    const unit = getUnit(category)
    const open = (consumer) =>
        navigate(buildListLink('/meter_readings', {consumer, ...todayRange()}))

    let content
    if (isLoading) {
        content = (
            <Stack spacing={2}>
                {[0, 1, 2, 3].map((key) => (
                    <Skeleton key={key} variant="rounded" height={36} />
                ))}
            </Stack>
        )
    } else if (!consumers.length) {
        content = (
            <Box sx={{flex: 1, display: 'grid', placeItems: 'center', minHeight: 200}}>
                <Typography variant="body2" color="text.secondary">
                    {t('dashboard.top.empty')}
                </Typography>
            </Box>
        )
    } else {
        content = (
            <Stack spacing={2.25}>
                {consumers.map(({consumer, name, usage}, index) => (
                    <Box
                        key={consumer || name}
                        role="link"
                        tabIndex={0}
                        onClick={() => open(consumer)}
                        onKeyDown={(event) => event.key === 'Enter' && open(consumer)}
                        sx={{
                            cursor: 'pointer',
                            borderRadius: 1,
                            mx: -1,
                            px: 1,
                            py: 0.5,
                            '&:hover': {backgroundColor: alpha(palette.primary.main, 0.05)},
                        }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                            <Typography variant="body2" fontWeight={600} noWrap sx={{pr: 2}}>
                                {index + 1}. {name}
                            </Typography>
                            <Typography variant="body2" fontWeight={700}>
                                {formatNumber(usage, 1)}{' '}
                                <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary">
                                    {unit}
                                </Typography>
                            </Typography>
                        </Stack>
                        <Box
                            sx={{
                                mt: 0.75,
                                height: 8,
                                borderRadius: 999,
                                backgroundColor: alpha(palette.primary.main, 0.1),
                            }}>
                            <Box
                                sx={{
                                    width: `${max ? (usage / max) * 100 : 0}%`,
                                    height: '100%',
                                    borderRadius: 999,
                                    backgroundColor: palette.primary.main,
                                }}
                            />
                        </Box>
                    </Box>
                ))}
            </Stack>
        )
    }

    return (
        <SectionCard title={t('dashboard.top.title')} subtitle={t('dashboard.top.subtitle')}>
            {content}
        </SectionCard>
    )
}

export default TopConsumers
